import React, {useContext, useState} from "react";
import {DraftContext} from "../draftSave/context/DraftSaveContext";
import Row from "@amzn/meridian/row";
import Modal, {ModalFooter} from "@amzn/meridian/modal";
import {getLocalizedMessage} from "../util/Localization";
import Text from "@amzn/meridian/text";
import Column from "@amzn/meridian/column";
import Select, {SelectOption} from "@amzn/meridian/select";
import Button from "@amzn/meridian/button";
import Icon from "@amzn/meridian/icon";
import trashTokens from "@amzn/meridian-tokens/base/icon/trash";
import escapeRegExp from "lodash/escapeRegExp"
import {injectIntl} from "react-intl";

// Generic delete modal for draft-staged configuration entries: a searchable select of the
// entries' keys, single delete, and delete-all. Parameterized so each config type supplies:
//   stateKey            - CONFIG_TYPE draft key
//   keyExtractor        - entry => unique display/compare key (e.g. entry => entry.label)
//   titleStringId        - modal title i18n id
//   promptStringId       - label above the select
//   placeholderStringId  - select placeholder
//   noMatchStringId      - shown when the search matches nothing
//   notAvailableStringId - shown when there is nothing staged at all
// TODO: migrate the near-identical per-device clones to this modal:
//   zancasort/modal/DeleteZancaSortLocationConfigurationModal, DeleteZancaSortTierConfigurationModal,
//   adtadevice/modal/DeleteAdtaSorterConfigurationModal + TestPackage variant,
//   adtfdevice/modal/DeleteAdtfSorterConfigurationModal + TestPackage variant,
//   autoloader/modal/DeleteAutoLoaderConfigurationModal + TestPackage variant,
//   asldevice/modal/DeleteAslGuidedDivertConfigurationModal,
//   tesseract/modal/DeleteTesseractStowConfigurationModal,
//   and commonui/DeleteTestPackageConfigurationModal (hardcodes the scannables keyExtractor).
const DeleteConfigurationModal = (props) => {

    const {getDraftData, deleteInstance, deleteAll} = useContext(DraftContext);
    const {open, setOpen, mac, intl, stateKey, keyExtractor,
        titleStringId, promptStringId, placeholderStringId,
        noMatchStringId, notAvailableStringId} = props;
    const [selectedKey, setSelectedKey] = useState("")
    const [searchQuery, setSearchQuery] = useState("")

    // getDraftData returns undefined (not []) when subKey/mac is missing; this component is
    // meant for reuse, so don't depend on every caller guarding the selection first.
    const keyList = (getDraftData({stateKey, subKey: mac}) || []).map(keyExtractor);

    const searchRegExp = new RegExp(escapeRegExp(searchQuery), "i")
    const matchedKeys = keyList.filter(
        option => !searchQuery || searchRegExp.test(option)
    )

    const onClickConfirm = () => {
        deleteInstance({
            stateKey,
            subKey: mac,
            keyComparator: (instance) => keyExtractor(instance) === selectedKey
        });
        setOpen(false)
        resetInputState();
    }

    function resetInputState() {
        setSelectedKey("");
        setSearchQuery("");
    }

    const onCloseModal = () => {
        setOpen(false);
        resetInputState();
    }

    return (
        <Row>
            <Modal
                title={getLocalizedMessage({intl, stringId: titleStringId})}
                open={open}
                onClose={onCloseModal}
                scrollContainer="viewport"
                closeLabel="Close"
                bodySpacingInset="medium">
                <Text tag="label" type="b200" color="primary" htmlFor="my-input">
                    {getLocalizedMessage({intl, stringId: promptStringId})}
                </Text>
                <Column spacing={"medium"} spacingInset={"medium"}>
                    <Select
                        value={selectedKey}
                        onChange={setSelectedKey}
                        searchQuery={searchQuery}
                        onSearch={setSearchQuery}
                        placeholder={getLocalizedMessage({intl, stringId: placeholderStringId})}
                        width="100%">
                        {matchedKeys.map((key) =>
                            <SelectOption key={key} value={key} label={key}/>
                        )}
                        {!matchedKeys.length ? (
                            <Column alignmentVertical="center" spacing="100" spacingInset="100">
                                {keyList.length ?
                                    <Text alignment="center">
                                        {getLocalizedMessage({intl, stringId: noMatchStringId})}
                                    </Text>
                                    : <Text alignment="center">
                                        {getLocalizedMessage({intl, stringId: notAvailableStringId})}
                                    </Text>
                                }
                            </Column>) : null}
                    </Select>
                </Column>
                <ModalFooter>
                    <Row alignmentHorizontal="justify" widths="fit">
                        <Button
                            size="small"
                            className="redBtn"
                            onClick={() => {
                                deleteAll({stateKey, subKey: mac});
                                onCloseModal();
                            }}>
                            <Icon tokens={trashTokens} />
                            {getLocalizedMessage({intl, stringId: "modal.deleteAll"})}
                        </Button>
                        <Row alignmentHorizontal="right" widths="fit">
                            <Button type="secondary" size="small" onClick={onCloseModal}>
                                {getLocalizedMessage({intl, stringId: "modal.cancel"})}
                            </Button>
                            <Button type="primary" size="small" onClick={onClickConfirm}>
                                {getLocalizedMessage({intl, stringId: "modal.confirm"})}
                            </Button>
                        </Row>
                    </Row>
                </ModalFooter>
            </Modal>
        </Row>
    )

}

export default injectIntl(DeleteConfigurationModal)



// WEBPACK FOOTER //
// ./src/components/commonui/DeleteConfigurationModal.js